/**
 * sqlite3mt reconcile import helpers
 *
 * Mechanical extraction from §96 §15.6–§15.8.
 *
 * This module owns the direct reconcile-import path under WAL_WRITE:
 * - tryImportPreparedIntentAsSingleWalCommit (§15.7)
 * - orphan-tail probe helpers (§15.8)
 * - APPENDING fatal transition helper (§11.2 / §15.7)
 */

import {
    Sab4Scalar,
    sab4ScalarView,
} from './sab-layout'
import type { DurableReconcileIntent } from './bootstrap'
import { abortIfFatal } from './control'
import {
    foldAllPendingIntoDbOverlay,
    insertPendingDeltaOrdered,
    noteObservedWalTipFromAppend,
} from './dbview'
import { publishFrontierStateToSabIfChanged } from './frontier'
import { isWalSnapshotUnstableError } from './shm-ops'
import type {
    AppendWalFramesMeta,
    ControlWorkerState,
    Delta,
    FrameMax,
    ImportTryResult,
    MergePlan,
    PageHash,
    Pgno,
    ReconcileProbeState,
    WalSnapshot,
} from './types'
import { sameWalSalt } from './types'
import { sdkConsole } from '../logger'

// ── §15.7 Error constants ─────────────────────────────────────

/** After durable APPENDING, the append outcome is unknown. Fatal. */
export const RECONCILE_APPEND_OUTCOME_UNKNOWN = 'RECONCILE_APPEND_OUTCOME_UNKNOWN'

/** Internal reentry guard for startPendingBaseAdvance. */
export const INTERNAL_PENDING_BASE_ADVANCE_REENTRY = 'INTERNAL_PENDING_BASE_ADVANCE_REENTRY'

/** Orphaned WAL tail detected during reconcile catch-up loop. Fatal. */
export const RECONCILE_ORPHANED_UNINTEGRATED_WAL_TAIL = 'RECONCILE_ORPHANED_UNINTEGRATED_WAL_TAIL'

/** Stuck outstanding commit post during reconcile. Fatal. */
export const RECONCILE_STUCK_OUTSTANDING_COMMIT_POST = 'RECONCILE_STUCK_OUTSTANDING_COMMIT_POST'

/** Import plan required a carrier frame but none was available. */
export const RECONCILE_MISSING_METADATA_CARRIER_FRAME = 'RECONCILE_MISSING_METADATA_CARRIER_FRAME'

/** Append returned unexpected frame position. */
export const RECONCILE_APPEND_FRAME_MISMATCH = 'RECONCILE_APPEND_FRAME_MISMATCH'

/** Imported page bytes could not be mapped back to a stable page hash. */
export const INTERNAL_RECONCILE_IMPORT_HASH_MISMATCH = 'INTERNAL_RECONCILE_IMPORT_HASH_MISMATCH'

/** Internal: catchUpMergePlanToFrame called beyond integrated frontier. */
export const INTERNAL_CATCHUP_BEYOND_INTEGRATED_FRONTIER = 'INTERNAL_CATCHUP_BEYOND_INTEGRATED_FRONTIER'

/** Failed to clear a PREPARED intent during reconcile cleanup. Fatal. */
export const PREPARED_INTENT_CLEAR_FAILED = 'PREPARED_INTENT_CLEAR_FAILED'

export interface ReconcileIntentAppendingAttempt {
    attemptStartFrame: number
    attemptEndFrame: number
    attemptWalSalt0: number
    attemptWalSalt1: number
}

export interface ReconcileImportDurableStore {
    markReconcileIntentAppending(
        intentId: bigint,
        attempt: ReconcileIntentAppendingAttempt
    ): Promise<void> | void
}

export interface ReconcileImportState extends Pick<
    ControlWorkerState,
    'dbOverlay'
    | 'dbPageCount'
    | 'pendingDbDeltas'
    | 'currentWalSalt0'
    | 'currentWalSalt1'
    | 'integratedFrameMax'
    | 'lastWalSnapshot'
> {}

export interface ReconcileImportRuntime {
    syncFatalError: unknown | null
}

export interface ReconcileImportHooks {
    tryAcquireWalWriteLock(): boolean
    releaseWalWriteLock(): void
    readWalSnapshotStable(): WalSnapshot
    preflightAppendWalFramesSingleTransaction?(
        importBlobsByPgno: ReadonlyMap<Pgno, Uint8Array>,
        targetPostPageCount: number
    ): ImportTryResult | null | undefined
    appendWalFramesSingleTransaction(
        importBlobsByPgno: ReadonlyMap<Pgno, Uint8Array>,
        targetPostPageCount: number
    ): AppendWalFramesMeta | Promise<AppendWalFramesMeta>
    rebuildWalIndexTail?(): void
    publishWalIndexHeaderDoubleCopy?(): void
    enterFatalRuntime(error: unknown, reason: string): void
}

export interface TryImportPreparedIntentAsSingleWalCommitArgs {
    intent: DurableReconcileIntent
    plan: MergePlan
    state: ReconcileImportState
    runtime: ReconcileImportRuntime
    durable: ReconcileImportDurableStore
    hooks: ReconcileImportHooks
    sab1: SharedArrayBuffer
    sab4: SharedArrayBuffer
    pageSize?: number
}

// ── §15.7 Import under WAL_WRITE ──────────────────────────────

export async function tryImportPreparedIntentAsSingleWalCommit({
    intent,
    plan,
    state,
    runtime,
    durable,
    hooks,
    sab1,
    sab4,
    pageSize,
}: TryImportPreparedIntentAsSingleWalCommitArgs): Promise<ImportTryResult> {
    if (!hooks.tryAcquireWalWriteLock()) {
        return { kind: 'LOCK_BUSY' }
    }

    const scalar = sab4ScalarView(sab4)
    Atomics.add(scalar, Sab4Scalar.WAL_WRITE_HOLDERS, 1)

    let walWriteHeld = true
    let appendingBecameDurable = false

    try {
        let live: WalSnapshot
        try {
            live = hooks.readWalSnapshotStable()
        } catch (error) {
            if (isWalSnapshotUnstableError(error)) {
                return { kind: 'LOCK_BUSY' }
            }
            throw error
        }

        if (!sameWalSalt(live.salt0, live.salt1, plan.walSalt0, plan.walSalt1)) {
            sdkConsole.warn('[sqlite3mt:reconcile] import stale epoch', {
                expectedWalSalt0: plan.walSalt0,
                expectedWalSalt1: plan.walSalt1,
                liveWalSalt0: live.salt0,
                liveWalSalt1: live.salt1,
                liveMxFrame: live.mxFrame,
                caughtUpFrameMax: plan.caughtUpFrameMax,
                stateWalSalt0: state.currentWalSalt0,
                stateWalSalt1: state.currentWalSalt1,
                stateIntegratedFrameMax: state.integratedFrameMax,
                stateLastWalSnapshot: state.lastWalSnapshot,
            })
            return { kind: 'STALE_EPOCH' }
        }

        if (live.mxFrame < plan.caughtUpFrameMax) {
            return { kind: 'STALE_RESET' }
        }

        if (live.mxFrame > plan.caughtUpFrameMax) {
            return { kind: 'STALE_TIP_GROW' }
        }

        if (plan.importBlobsByPgno.size === 0) {
            if (plan.resultPageCount !== plan.liveLocalPageCount) {
                throw new Error(RECONCILE_MISSING_METADATA_CARRIER_FRAME)
            }
            return { kind: 'OK', appendMeta: null }
        }

        const attemptStartFrame = live.mxFrame + 1
        const attemptEndFrame = attemptStartFrame + plan.importBlobsByPgno.size - 1
        const preflightResult = hooks.preflightAppendWalFramesSingleTransaction?.(
            plan.importBlobsByPgno,
            plan.resultPageCount,
        )
        if (preflightResult?.kind === 'WAL_CAPACITY_BUSY') {
            sdkConsole.warn('[sqlite3mt:reconcile] import wal capacity busy before APPENDING', preflightResult)
            return preflightResult
        }

        await durable.markReconcileIntentAppending(intent.intentId, {
            attemptStartFrame,
            attemptEndFrame,
            attemptWalSalt0: plan.walSalt0,
            attemptWalSalt1: plan.walSalt1,
        })

        appendingBecameDurable = true
        intent.phase = 'APPENDING'
        intent.attemptStartFrame = attemptStartFrame
        intent.attemptEndFrame = attemptEndFrame
        intent.attemptWalSalt0 = plan.walSalt0
        intent.attemptWalSalt1 = plan.walSalt1

        abortIfFatal(runtime)

        const appendResult = await hooks.appendWalFramesSingleTransaction(
            plan.importBlobsByPgno,
            plan.resultPageCount
        )

        if (appendResult.commitFrameMax !== attemptEndFrame) {
            throw new Error(RECONCILE_APPEND_FRAME_MISMATCH)
        }

        hooks.rebuildWalIndexTail?.()
        hooks.publishWalIndexHeaderDoubleCopy?.()

        const importDelta = buildImportDeltaFromPlan(plan)

        if (state.currentWalSalt0 == null) {
            state.currentWalSalt0 = appendResult.postWalSalt0
            state.currentWalSalt1 = appendResult.postWalSalt1
            state.integratedFrameMax = 0
        } else if (!sameWalSalt(
            state.currentWalSalt0,
            state.currentWalSalt1,
            appendResult.postWalSalt0,
            appendResult.postWalSalt1
        )) {
            const dbPageCountRef = { value: state.dbPageCount }
            foldAllPendingIntoDbOverlay(
                sab1,
                state.pendingDbDeltas,
                state.dbOverlay,
                dbPageCountRef,
                pageSize
            )
            state.dbPageCount = dbPageCountRef.value
            state.currentWalSalt0 = appendResult.postWalSalt0
            state.currentWalSalt1 = appendResult.postWalSalt1
            state.integratedFrameMax = 0
        }

        noteObservedWalTipFromAppend(
            state,
            appendResult.postWalSalt0,
            appendResult.postWalSalt1,
            appendResult.commitFrameMax
        )

        insertPendingDeltaOrdered(
            state.pendingDbDeltas,
            appendResult.commitFrameMax as FrameMax,
            importDelta
        )

        if (appendResult.commitFrameMax > state.integratedFrameMax) {
            state.integratedFrameMax = appendResult.commitFrameMax
            publishFrontierFromState(sab4, state)
        }

        return {
            kind: 'OK',
            appendMeta: {
                commitFrameMax: appendResult.commitFrameMax,
                postWalSalt0: appendResult.postWalSalt0,
                postWalSalt1: appendResult.postWalSalt1,
            },
        }
    } catch (error) {
        if (appendingBecameDurable) {
            sdkConsole.error('[sqlite3mt:reconcile] import failed after APPENDING became durable', {
                error: error instanceof Error ? {
                    name: error.name,
                    message: error.message,
                    stack: error.stack,
                } : String(error),
                intentId: intent.intentId.toString(),
                attemptStartFrame: intent.attemptStartFrame,
                attemptEndFrame: intent.attemptEndFrame,
                planImportPageCount: plan.importBlobsByPgno.size,
                resultPageCount: plan.resultPageCount,
            })
            enterUncertainAppendingFatal(hooks.enterFatalRuntime)
        }
        throw error
    } finally {
        if (walWriteHeld) {
            walWriteHeld = false
            Atomics.sub(scalar, Sab4Scalar.WAL_WRITE_HOLDERS, 1)
            hooks.releaseWalWriteLock()
        }
    }
}

// ── §15.8 Reconcile catch-up helpers ──────────────────────────

/**
 * §15.8: Can the WAL tail still be expected to integrate soon?
 *
 * Returns true when there are active WAL_WRITE holders or inflight commit
 * registrations, meaning pending deltas may appear shortly.
 */
export function reconcileTailCanStillIntegrateSoon(
    sab4: SharedArrayBuffer,
    inflightCommitRegistrations: number
): boolean {
    return Atomics.load(sab4ScalarView(sab4), Sab4Scalar.WAL_WRITE_HOLDERS) > 0
        || inflightCommitRegistrations > 0
}

/**
 * §15.8: Effective integrated frontier for reconcile tail decisions.
 *
 * A live WAL prefix that is already backfilled into the visible MainDb is
 * effectively integrated even if frontier bookkeeping has not yet advanced.
 */
export function reconcileEffectiveIntegratedFrameMax(
    live: Pick<WalSnapshot, 'nBackfill'>,
    integratedFrameMax: number
): number {
    return Math.max(integratedFrameMax, live.nBackfill)
}

/**
 * §15.8: Capture a probe of WAL + frontier state for orphan detection.
 *
 * The probe is compared across successive checks to detect a stable
 * unintegrable state (same WAL position, same frontier, no progress).
 */
export function reconcileOrphanProbeState(
    live: Pick<WalSnapshot, 'salt0' | 'salt1' | 'mxFrame' | 'nBackfill'>,
    integratedFrameMax: number
): ReconcileProbeState {
    return {
        salt0: live.salt0,
        salt1: live.salt1,
        liveMxFrame: live.mxFrame,
        integratedFrameMax: reconcileEffectiveIntegratedFrameMax(live, integratedFrameMax),
    }
}

/**
 * §15.8: Compare two probe states for exact equality.
 *
 * When two successive probes match exactly and no WAL_WRITE holders
 * or inflight registrations exist, the WAL tail is orphaned.
 */
export function sameReconcileOrphanProbeState(
    a: ReconcileProbeState,
    b: ReconcileProbeState
): boolean {
    return a.salt0 === b.salt0
        && a.salt1 === b.salt1
        && a.liveMxFrame === b.liveMxFrame
        && a.integratedFrameMax === b.integratedFrameMax
}

// ── §11.2 / §15.7 APPENDING fatal helper ──────────────────────

/**
 * Enter uncertain APPENDING fatal state (§11.2, §15.7, R34).
 *
 * After a durable APPENDING write, the physical append outcome is unknown.
 * The runtime must enter fatal state. The intent is left durable for
 * bootstrap resolution — it must NOT be cleared by the current runtime.
 *
 * This is a thin wrapper that calls the provided enterFatalRuntime callback
 * with the canonical APPENDING-outcome error. The caller owns the actual
 * fatal transition and wakeFatalWaiters logic.
 */
export function enterUncertainAppendingFatal(
    enterFatalRuntime: (error: unknown, reason: string) => void
): void {
    enterFatalRuntime(
        RECONCILE_APPEND_OUTCOME_UNKNOWN,
        RECONCILE_APPEND_OUTCOME_UNKNOWN
    )
}

export function buildImportDeltaFromPlan(plan: MergePlan): Delta {
    const pageHashes = new Map<Pgno, PageHash>()

    for (const [pgno, bytes] of plan.importBlobsByPgno) {
        pageHashes.set(pgno, resolveImportPageHash(plan, pgno, bytes))
    }

    return {
        pageHashes,
        postPageCount: plan.resultPageCount,
    }
}

function resolveImportPageHash(
    plan: MergePlan,
    pgno: Pgno,
    bytes: Uint8Array
): PageHash {
    const resultHash = plan.resultHashByPgno.get(pgno)
    if (resultHash != null) {
        return resultHash
    }

    let foundHash: PageHash | null = null
    for (const [hash, candidate] of plan.importBlobsByHash) {
        if (!sameBytes(candidate, bytes)) {
            continue
        }

        if (foundHash != null && foundHash !== hash) {
            throw new Error(INTERNAL_RECONCILE_IMPORT_HASH_MISMATCH)
        }
        foundHash = hash
    }

    if (foundHash == null) {
        throw new Error(INTERNAL_RECONCILE_IMPORT_HASH_MISMATCH)
    }

    return foundHash
}

function sameBytes(left: Uint8Array, right: Uint8Array): boolean {
    if (left === right) {
        return true
    }
    if (left.byteLength !== right.byteLength) {
        return false
    }
    for (let index = 0; index < left.byteLength; index += 1) {
        if (left[index] !== right[index]) {
            return false
        }
    }
    return true
}

function publishFrontierFromState(
    sab4: SharedArrayBuffer,
    state: Pick<ReconcileImportState, 'currentWalSalt0' | 'currentWalSalt1' | 'integratedFrameMax'>
): void {
    publishFrontierStateToSabIfChanged(sab4, {
        currentWalSalt0: state.currentWalSalt0,
        currentWalSalt1: state.currentWalSalt1,
        integratedFrameMax: state.integratedFrameMax,
    })
}
